import { focusManager } from "@tanstack/react-query"
import { Modal } from "antd"
import { lazy, memo, Suspense, useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { submitNPSSurvey, updateUserFeatureFlags } from "~/api/user"
import { useGetAccountOSActionsOfTheDay } from "~/hooks/accountos-overview"
import { useCheckEmailConnection } from "~/hooks/user"
import useGlobalStore from "~/store/global"
import useOsLiteStore from "~/store/os-lite"
import { track } from "~/utils/amplitude"

const InitalizeFormBricks = lazy(
  () => import("~/containers/initalize-formbricks")
)
const ConnectEmailProviders = lazy(
  () => import("~/components/connect-email-provider")
)
const WhatsNewModal = lazy(() => import("./whats-new"))

const McpConsentModal = lazy(() => import("./mcp-consent"))

const OsLiteAddAccounts = lazy(() => import("./os-lite-add-accounts"))

function RootQueries() {
  const { t } = useTranslation()
  const heartBeatAmplitudeIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const connectEmailProviderModalOpen = useGlobalStore(
    (store) => store.connectEmailProviderModalOpen
  )
  const npsSurveyStatus = useGlobalStore((store) => store.npsSurveyStatus)
  const addAccounts = useOsLiteStore((store) => store.addAccounts)

  const surveySubmittedRef = useRef(false)
  const [modalApi, modalContextHolder] = Modal.useModal()
  useCheckEmailConnection()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  useGetAccountOSActionsOfTheDay("180 days")

  useEffect(() => {
    if (window.top && window.top !== window.self) {
      window.top.location.href = window.location.href
    }
  }, [])

  const handleMessageEvents = useCallback(async (event: MessageEvent) => {
    if (
      event.data === "formbricksSurveyCompleted" &&
      !surveySubmittedRef.current &&
      tokenInfo
    ) {
      try {
        await submitNPSSurvey(tokenInfo)
        await updateUserFeatureFlags(tokenInfo, {
          nps_survey_completed: true
        })
        surveySubmittedRef.current = true
        import("@formbricks/js").then(({ default: formbricks }) => {
          formbricks.logout()
        })
        localStorage.removeItem("formbricks-js")
      } catch {
        // do nothing
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let offlineModal: any

    function startAmplitudeHeartBeat() {
      if (heartBeatAmplitudeIntervalRef.current) return

      heartBeatAmplitudeIntervalRef.current = setInterval(() => {
        track("heartbeat")
      }, 30000)
    }

    function stopAmplitudeHeartBeat() {
      if (heartBeatAmplitudeIntervalRef.current) {
        clearInterval(heartBeatAmplitudeIntervalRef.current)
        heartBeatAmplitudeIntervalRef.current = null
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && navigator.onLine) {
        focusManager.setFocused(true)
        startAmplitudeHeartBeat()
      } else {
        stopAmplitudeHeartBeat()
      }
    }

    function handleOnline() {
      offlineModal?.destroy()
      offlineModal = null
      if (document.visibilityState === "visible") {
        startAmplitudeHeartBeat()
      }
    }
    function handleOffline() {
      stopAmplitudeHeartBeat()
      offlineModal = modalApi.error({
        title: t("label_you_are_offline"),
        okText: t("label_ok"),
        content: (
          <p className="text-13">
            {t("label_offline_check_connection_try_again")}
          </p>
        ),
        centered: true,
        closable: false,
        footer: null,
        classNames: {
          content: "!p-5"
        }
      })
    }

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    window.addEventListener("visibilitychange", handleVisibilityChange)

    window.addEventListener("message", handleMessageEvents)

    if (document.visibilityState === "visible" && navigator.onLine) {
      startAmplitudeHeartBeat()
    }

    return () => {
      stopAmplitudeHeartBeat()
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
      window.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("message", handleMessageEvents, false)
      offlineModal?.destroy()
      surveySubmittedRef.current = false
    }
  }, [modalApi, handleMessageEvents])

  return (
    <Suspense fallback={null}>
      {modalContextHolder}
      {connectEmailProviderModalOpen && <ConnectEmailProviders />}
      <WhatsNewModal />
      <McpConsentModal />
      {addAccounts && <OsLiteAddAccounts />}
      {npsSurveyStatus?.eligible && npsSurveyStatus?.survey_id && (
        <InitalizeFormBricks />
      )}
    </Suspense>
  )
}

export default memo(RootQueries)
